import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, User, CreditCard, Shield, Briefcase, Gift, Palette, Key, Plug, Rocket, TestTube, Bell } from 'lucide-react';
import ProfileTab from './ProfileTab.js';
import BillingTab from './BillingTab.js';
import WorkspaceTab from './WorkspaceTab.js';
import ReferralsTab from './ReferralsTab.js';
import CustomizationTab from './CustomizationTab.js';
import SecurityTab from './SecurityTab.js';
import SecretsTab from './SecretsTab.js';
import IntegrationsTab from './IntegrationsTab.js';
import DeploymentsTab from './DeploymentsTab.js';
import QATestingTab from './QATestingTab.js';
import NotificationsTab from './NotificationsTab.js';
import EnvValidationTab from './EnvValidationTab.js';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  fullScreen?: boolean;
  initialTab?: SettingsTab;
}

type SettingsTab = 'profile' | 'billing' | 'workspace' | 'referrals' | 'customization' | 'notifications' | 'security' | 'secrets' | 'integrations' | 'deployments' | 'qa-testing' | 'env-validation';

const tabs: Array<{
  id: SettingsTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'workspace', label: 'Workspace', icon: Briefcase },
  { id: 'referrals', label: 'Referrals', icon: Gift },
  { id: 'customization', label: 'Customization', icon: Palette },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'secrets', label: 'Secrets', icon: Key },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'deployments', label: 'Deployments', icon: Rocket },
  { id: 'qa-testing', label: 'QA Testing', icon: TestTube },
  { id: 'env-validation', label: 'Environment', icon: Shield },
];

const SettingsPanel: React.FC<SettingsPanelProps> = ({ isOpen, onClose, fullScreen = false, initialTab = 'profile' }) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab />;
      case 'billing':
        return <BillingTab />;
      case 'workspace':
        return <WorkspaceTab />;
      case 'referrals':
        return <ReferralsTab />;
      case 'customization':
        return <CustomizationTab />;
      case 'notifications':
        return <NotificationsTab />;
      case 'security':
        return <SecurityTab />;
      case 'secrets':
        return <SecretsTab />;
      case 'integrations':
        return <IntegrationsTab />;
      case 'deployments':
        return <DeploymentsTab />;
      case 'qa-testing':
        return <QATestingTab />;
      case 'env-validation':
        return <EnvValidationTab />;
      default:
        return <ProfileTab />;
    }
  };

  if (fullScreen) {
    return (
      <div className="flex flex-col h-full w-full bg-slate-900 overflow-hidden" data-testid="settings-panel">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-violet-400" />
            <h2 className="text-lg sm:text-xl font-semibold text-white">Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Tab Navigation */}
          <div className="w-48 sm:w-56 lg:w-64 flex-shrink-0 border-r border-slate-700 p-2 sm:p-4 overflow-y-auto bg-slate-900">
            <nav className="space-y-1 sm:space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`settings-tab-${tab.id}`}
                    className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-violet-600 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <Icon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-medium truncate">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="flex-1 min-w-0 p-4 sm:p-6 overflow-y-auto bg-slate-900">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-none lg:max-w-4xl xl:max-w-5xl"
            >
              {renderTabContent()}
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          {/* Settings Modal */}
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-[90vw] sm:w-[80vw] md:w-[70vw] lg:w-[60vw] xl:w-[800px] max-w-[90vw] bg-slate-900 border-l border-slate-700 z-50 flex flex-col shadow-2xl"
            data-testid="settings-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-violet-400" />
                <h2 className="text-lg sm:text-xl font-semibold text-white">Settings</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>

            <div className="flex flex-1 min-h-0 overflow-hidden">
              {/* Tab Navigation */}
              <div className="w-48 sm:w-56 lg:w-64 flex-shrink-0 border-r border-slate-700 p-2 sm:p-4 overflow-y-auto bg-slate-900">
                <nav className="space-y-1 sm:space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        data-testid={`settings-tab-${tab.id}`}
                        className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 rounded-lg text-left transition-colors ${
                          activeTab === tab.id
                            ? 'bg-violet-600 text-white'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <Icon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium truncate">{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="flex-1 min-w-0 p-4 sm:p-6 overflow-y-auto bg-slate-900">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="w-full max-w-none lg:max-w-4xl xl:max-w-5xl"
                >
                  {renderTabContent()}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SettingsPanel;
